import { PARENT_BRIDGE_VERSION, runSafely } from '@/services/parentBridge/parentBridge';
import { isTrustedParentOrigin, getTrustedParentOrigins } from '@/services/parentBridge/parentOrigins';

/**
 * Commands the embedding page can send to the sportsbook (parent bridge v1, same
 * envelope as the events: {type, v, payload}).
 *
 * Slip: remove one selection, clear the slip, set the stake. Each runs the same cart
 * actions as the player's own input (BetOddsSummaryMarketViewV3.removeOdd,
 * BetTypeSelectorMarketView.clearCart, the stake field of the slip summary: write the
 * amount, then refreshCart). Ignored while the slip is busy (a bet being placed, the slip
 * updating, a receipt shown, a bet waiting for review or for the player's answer to a
 * proposal), where the slip itself does not let the player change it.
 *
 * Stake (SPORT_SLIP_SET_STAKE {stake}): set only, never add or subtract, so a message
 * received twice cannot change the stake twice. Only when the skin lists its trusted
 * parent sites, on a single or a multiple with selections and no free bet chosen. A number
 * >= 0, two decimals, raised to the skin's minimum as the stake field does on blur. Place
 * bet then waits STAKE_LOCK_MS (cart parent_stake_lock), so the player sees the new amount
 * before the click. rocky-bets checks it at placement like any stake (min/max, max win).
 *
 * Navigation: open a tournament or an event without reloading the iframe. The layout
 * template does it with _handleSlugNavigation, the same code that restores the page
 * after a reload (it listens to PARENT_NAVIGATE_EVENT on the root instance).
 *
 * Nothing here accepts odds or places a bet. Every command is accepted only from the page
 * that embeds the sportsbook (event.source is window.parent) and, when the skin lists its
 * trusted parent sites (parentOrigins), only from one of them (event.origin).
 */
export const PARENT_COMMANDS = Object.freeze({
    SLIP_REMOVE:    'SPORT_SLIP_REMOVE',
    SLIP_CLEAR:     'SPORT_SLIP_CLEAR',
    SLIP_SET_STAKE: 'SPORT_SLIP_SET_STAKE',
    NAVIGATE:       'SPORT_NAVIGATE',
});

// How long place bet waits after the embedding page sets the stake
export const STAKE_LOCK_MS = 1000;

const STAKE_PATTERN = /^\d+(\.\d+)?$/;

// Emitted on the root instance with the slug to open; TemplateMarketView / American / Terminal listen
export const PARENT_NAVIGATE_EVENT = 'parentNavigate';

const COMMAND_TYPES = Object.values(PARENT_COMMANDS);

// Feed ids are numbers, manual ones are M-prefixed: anything else is not an id
const ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
const MAX_NAME_LENGTH = 200;

const idOrNull = (value) => {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const id = String(value);
    return ID_PATTERN.test(id) ? id : null;
};

const nameOrEmpty = (value) => (typeof value === 'string' ? value.slice(0, MAX_NAME_LENGTH) : '');

/**
 * The slug of a SPORT_NAVIGATE payload, in the shape _handleSlugNavigation reads (the same
 * as the slug of SPORT_NAVIGATION_CHANGED). A tournament needs sportId and tournamentId;
 * an event adds eventId, and isLive opens its live page.
 *
 * @param {object} payload
 * @returns {object|null} null when the payload does not name a tournament or an event
 */
export function navigationSlug(payload) {
    const p = payload || {};
    const sportId = idOrNull(p.sportId);
    const tournamentId = idOrNull(p.tournamentId);
    if (!sportId || !tournamentId) return null;
    if (p.eventId !== undefined && p.eventId !== null && p.eventId !== '' && !idOrNull(p.eventId)) return null;
    const eventId = idOrNull(p.eventId);
    const isLive = eventId !== null && p.isLive === true;
    return {
        page:           eventId ? (isLive ? 'live_detail' : 'prematch_detail') : 'tournament',
        sportId,
        tournamentId,
        eventId,
        categoryId:     idOrNull(p.categoryId),
        sportName:      nameOrEmpty(p.sportName),
        tournamentName: nameOrEmpty(p.tournamentName),
        eventName:      nameOrEmpty(p.eventName),
        isLive,
    };
}

/**
 * @param {object} store - the Vuex store
 * @returns {string|null} why the slip cannot take a command now, null when it can
 */
export function slipBusyReason(store) {
    const cart = store.state.cart || {};
    if (store.getters['cart/getLoading'] === true) return 'placing or loading';
    if (store.getters['cart/getIsCartUpdating'] === true) return 'updating';
    if (cart.confirmed_bet) return 'receipt shown';
    if ((cart.accepting_bets || []).length > 0) return 'bet in review';
    if ((cart.accepted_with_change || []).length > 0) return 'proposal waiting for the player';
    return null;
}

// The player's "remove" on one selection (a leg of a bet builder group included)
async function removeSelection(store, root, selectionId) {
    const odd = (store.state.cart.bet.odds || []).find(o => String(o.unique_id) === selectionId);
    if (!odd) {
        console.warn(`[ParentBridge] ${PARENT_COMMANDS.SLIP_REMOVE}: ${selectionId} is not in the slip, ignored`);
        return;
    }
    await store.dispatch('cart/removeOddFromCart', { unique_id: odd.unique_id });
    await store.dispatch('cart/checkFixedBaseSystem', { unique_id: odd.unique_id });
    root.$emit('forceUpdateSystem');
    root.$emit('forceUpdateSingleMultiple');
    root.$emit('forceUpdateIntegral');
}

// The player's "clear all"
async function clearSlip(store, root) {
    root.$emit('cart_handling', true);
    await store.dispatch('cart/resetCart');
}

/**
 * The stake of a SPORT_SLIP_SET_STAKE payload.
 *
 * @param {*} value - a number, or the same written as a string ("30", "12.5")
 * @returns {number|null} rounded to two decimals; null when it is not a number >= 0
 */
export function stakeValue(value) {
    let n;
    if (typeof value === 'number') n = value;
    else if (typeof value === 'string' && STAKE_PATTERN.test(value.trim())) n = Number(value.trim());
    else return null;
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100) / 100;
}

/**
 * @param {object} store - the Vuex store
 * @returns {string|null} why the stake cannot be set now, null when it can
 */
export function stakeRefusalReason(store) {
    const cart = store.state.cart || {};
    const bet = cart.bet || {};
    if (getTrustedParentOrigins().length === 0) return 'the skin lists no trusted parent sites (parentOrigins)';
    if ((bet.odds || []).length === 0) return 'the slip is empty';
    if (bet.type !== 'single' && bet.type !== 'multiple') return `a ${bet.type} bet has a stake per combination`;
    if (cart.selected_free_bet_id) return 'a free bet is chosen';
    return null;
}

let stakeLockTimer = null;

// Place bet waits STAKE_LOCK_MS; a new stake in the meantime starts the wait again
function lockPlaceBet(store) {
    store.commit('cart/SET_PARENT_STAKE_LOCK', true);
    clearTimeout(stakeLockTimer);
    stakeLockTimer = setTimeout(() => {
        stakeLockTimer = null;
        runSafely('stake lock release', () => store.commit('cart/SET_PARENT_STAKE_LOCK', false));
    }, STAKE_LOCK_MS);
}

// The stake field of the slip: the amount, raised to the skin's minimum as on blur, then refreshCart
async function setStake(store, stake) {
    const cart = store.state.cart;
    const min = Number((cart.settings || {}).minAmount) || 0;
    const amount = Math.max(stake, min);
    if (Number((cart.bet._multiple_detail || {}).amount) === amount) return;
    lockPlaceBet(store);
    store.commit('cart/SET_MULTIPLE_AMOUNT', amount);
    await store.dispatch('cart/refreshCart');
}

// What the command asks, or null (logged) when it is not acceptable now
function readCommand(data, store) {
    const payload = data.payload || {};
    if (data.type === PARENT_COMMANDS.NAVIGATE) {
        const slug = navigationSlug(payload);
        if (!slug) {
            console.warn(`[ParentBridge] ${data.type} ignored: sportId and tournamentId (and a valid eventId, if any) are required`);
            return null;
        }
        return { type: data.type, slug };
    }
    const busy = slipBusyReason(store);
    if (busy) {
        console.warn(`[ParentBridge] ${data.type} ignored: ${busy}`);
        return null;
    }
    if (data.type === PARENT_COMMANDS.SLIP_SET_STAKE) {
        const stake = stakeValue(payload.stake);
        const refusal = stakeRefusalReason(store) || (stake === null ? 'stake must be a number >= 0' : null);
        if (refusal) {
            console.warn(`[ParentBridge] ${data.type} ignored: ${refusal}`);
            return null;
        }
        return { type: data.type, stake };
    }
    if (data.type === PARENT_COMMANDS.SLIP_REMOVE && (payload.selectionId === undefined || payload.selectionId === null || payload.selectionId === '')) {
        console.warn(`[ParentBridge] ${data.type} ignored: selectionId missing`);
        return null;
    }
    return { type: data.type, selectionId: payload.selectionId != null ? String(payload.selectionId) : null };
}

/**
 * @param {{store: object, root: object, win?: Window}} deps - root is the app's root Vue instance
 * @returns {function(MessageEvent): Promise<void>} the `message` listener
 */
export function createParentCommandHandler({ store, root, win = window }) {
    return async (event) => {
        let command = null;
        runSafely('parent command', () => {
            const data = event && event.data;
            if (!data || typeof data !== 'object' || data.v !== PARENT_BRIDGE_VERSION) return;
            if (!COMMAND_TYPES.includes(data.type)) return;
            if (!win.parent || win.parent === win || event.source !== win.parent) {
                console.warn(`[ParentBridge] ${data.type} ignored: not sent by the embedding page`);
                return;
            }
            if (!isTrustedParentOrigin(event.origin)) {
                console.warn(`[ParentBridge] ${data.type} ignored: ${event.origin} is not in the skin's parentOrigins`);
                return;
            }
            command = readCommand(data, store);
        });
        if (!command) return;

        try {
            if (command.type === PARENT_COMMANDS.NAVIGATE) root.$emit(PARENT_NAVIGATE_EVENT, command.slug);
            else if (command.type === PARENT_COMMANDS.SLIP_REMOVE) await removeSelection(store, root, command.selectionId);
            else if (command.type === PARENT_COMMANDS.SLIP_SET_STAKE) await setStake(store, command.stake);
            else await clearSlip(store, root);
        } catch (err) {
            console.error(`[ParentBridge] ${command.type} failed:`, err);
        }
    };
}

let installed = false;

/**
 * Starts listening to the commands, once per page.
 *
 * @param {{store: object, root: object}} deps
 */
export function installParentCommands({ store, root }) {
    runSafely('parent commands install', () => {
        if (installed || typeof window === 'undefined' || window.parent === window) return;
        window.addEventListener('message', createParentCommandHandler({ store, root }), false);
        installed = true;
    });
}
